import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useExpensesService } from 'services/expenses';
import { useExpensesData } from 'hooks/useExpensesData';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import { Expense as ExpenseType } from 'types/expenses';
import Category from 'types/category';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import { Search, Eye, Trash2 } from 'lucide-react';
import VoidExpensesModal from './VoidExpensesModal';
import styles from './Expenses.module.scss';

function Expenses() {
  const navigate = useNavigate();
  const expensesService = useExpensesService();
  const apiClient = useApiClient();
  const [categories, setCategories] = useState<{ [key: string]: Category }>({});
  const [includeVoided, setIncludeVoided] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [expenseToVoid, setExpenseToVoid] = useState<ExpenseType | null>(null);
  const [isVoiding, setIsVoiding] = useState(false);

  const { expenses, loading, reloading, error, reload, updateExpense } =
    useExpensesData({ includeVoided });

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await apiClient.get<{ [key: string]: Category }>(
          API_ROUTES.CATEGORY,
        );
        if (categoriesData) {
          setCategories(categoriesData);
        }
      } catch {
        setCategories({});
      }
    };

    void loadCategories();
  }, [apiClient]);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const filteredExpenses = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return expenses.filter((expense) => {
      const matchesSearch =
        expense.description.toLowerCase().includes(searchLower) ||
        expense.date.toLowerCase().includes(searchLower) ||
        expense.expenseEntries.some((entry) => {
          const category = categories[entry.categoryId];
          return category?.name?.toLowerCase().includes(searchLower) ?? false;
        }) ||
        expense.cashOrApEntries.some((entry) => {
          const category = categories[entry.categoryId];
          return category?.name?.toLowerCase().includes(searchLower) ?? false;
        });
      return matchesSearch;
    });
  }, [expenses, searchQuery, categories]);

  const totalFilteredExpenses = filteredExpenses.length;

  const paginatedExpenses = useMemo(() => {
    if (totalFilteredExpenses === 0) {
      return [];
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredExpenses.slice(startIndex, endIndex);
  }, [filteredExpenses, currentPage, pageSize, totalFilteredExpenses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeVoided]);

  useEffect(() => {
    const nextTotalPages = Math.max(
      1,
      Math.ceil(totalFilteredExpenses / pageSize),
    );
    setCurrentPage((prevPage) => Math.min(prevPage, nextTotalPages));
  }, [totalFilteredExpenses, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleViewExpense = (expense: ExpenseType) => {
    navigate(`/expenses/${expense.id}`);
  };

  const handleVoidExpense = (expense: ExpenseType) => {
    setExpenseToVoid(expense);
  };

  const handleCloseVoidModal = () => {
    setExpenseToVoid(null);
  };

  const handleConfirmVoid = useCallback(
    async (reason: string) => {
      if (!expenseToVoid) return;

      setIsVoiding(true);
      try {
        const voidedExpense = await expensesService.voidExpense(
          expenseToVoid.id,
          reason || undefined,
        );
        updateExpense(voidedExpense);
        showToast('Expense voided successfully', 'success');
        setExpenseToVoid(null);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to void expense. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setIsVoiding(false);
      }
    },
    [expenseToVoid, expensesService, showToast, updateExpense],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Expenses</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}>Loading expenses...</div>
      ) : (
        <div
          className={`${styles.contentWrapper} ${
            reloading ? styles.contentReloading : ''
          }`}
        >
          {reloading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}>Loading...</div>
            </div>
          )}

          <div className={styles.filters}>
            <div className={styles.searchContainer}>
              <Search size={18} className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                className={styles.searchInput}
              />
            </div>
            <label className={styles.filterLabel}>
              <input
                type="checkbox"
                checked={includeVoided}
                onChange={(e) => {
                  setIncludeVoided(e.target.checked);
                }}
                className={styles.filterCheckbox}
              />
              Include Voided
            </label>
          </div>

          {error && expenses.length === 0 && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <Button onClick={reload}>Retry</Button>
            </div>
          )}

          {!loading && filteredExpenses.length === 0 && !error && (
            <div className={styles.emptyState}>
              <p>
                {searchQuery || includeVoided
                  ? 'No expenses found matching your filters.'
                  : 'No expenses yet.'}
              </p>
            </div>
          )}

          {filteredExpenses.length > 0 && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Total Expense</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedExpenses.map((expense) => (
                    <tr key={expense.id}>
                      <td className={styles.dateCell}>
                        {formatDate(expense.date)}
                      </td>
                      <td className={styles.descriptionCell}>
                        {expense.description || 'No description'}
                      </td>
                      <td className={styles.amountCell}>
                        ${formatCurrency(expense.totalExpense)}
                      </td>
                      <td className={styles.statusCell}>
                        {expense.isVoided ? (
                          <span className={styles.voidedBadge}>Voided</span>
                        ) : (
                          <span className={styles.activeBadge}>Active</span>
                        )}
                      </td>
                      <td className={styles.actionsCell}>
                        <button
                          className={styles.viewButton}
                          onClick={() => {
                            handleViewExpense(expense);
                          }}
                          aria-label={`View expense ${expense.id}`}
                          title="View expense"
                        >
                          <Eye size={16} />
                        </button>
                        {!expense.isVoided && (
                          <button
                            className={styles.voidButton}
                            onClick={() => {
                              handleVoidExpense(expense);
                            }}
                            aria-label={`Void expense ${expense.id}`}
                            title="Void expense"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalFilteredExpenses > 0 && (
            <div className={styles.paginationWrapper}>
              <Pagination
                currentPage={currentPage}
                totalItems={totalFilteredExpenses}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}

          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => {
                setToast(null);
              }}
            />
          )}

          <VoidExpensesModal
            show={!!expenseToVoid}
            onHide={handleCloseVoidModal}
            expense={expenseToVoid}
            onConfirm={(reason) => void handleConfirmVoid(reason)}
            isPending={isVoiding}
          />
        </div>
      )}
    </div>
  );
}

export default Expenses;
